import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Loader2, AlertTriangle, RefreshCw, Briefcase, Users, CalendarDays, ListTodo, IndianRupee, Boxes } from 'lucide-react';

// CEO Cockpit v1 - Phase 2 of the JM->ATS migration.
// One position from which the whole business is visible.

// Rupees, Indian grouping, no decimals: 99960 -> "Rs 99,960". Phase 5 (Record the money).
const inr = (n) => 'Rs ' + Math.round(Number(n) || 0).toLocaleString('en-IN');

const Stat = ({ label, value, sub, to }) => {
  const body = (
    <div className="bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors h-full">
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-semibold text-slate-900 mt-1">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
};

const Section = ({ title, icon: Icon, children, accent }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5">
    <div className="flex items-center gap-2 mb-4">
      {Icon && <Icon className={`w-4 h-4 ${accent || 'text-slate-500'}`} />}
      <h2 className="text-sm font-semibold text-slate-800">{title}</h2>
    </div>
    {children}
  </div>
);

export default function CEOPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setError('');
      const res = await api.get('/ceo/summary');
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load CEO summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="flex items-center gap-2 p-4 text-sm text-red-600 bg-red-50 rounded-lg max-w-lg">
          <AlertTriangle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  const d = data || {};
  const cand = d.candidates || {};
  const byStatus = cand.by_status || {};
  const pipelineOrder = ['uploaded', 'screened', 'shortlisted', 'lined_up', 'interviewed', 'offered', 'joined', 'rejected'];
  const attention = d.needs_attention || [];
  const feeds = d.business_feeds || [];
  const imp = d.imported_from_jm || {};

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-7xl mx-auto max-md:pb-[calc(1.5rem+env(safe-area-inset-bottom))]" data-testid="ceo-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">CEO Cockpit</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            The whole business from one position · refreshed {d.generated_at ? new Date(d.generated_at).toLocaleTimeString() : ''}
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 text-xs text-slate-600 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50"
          data-testid="ceo-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Open Requirements" value={d.requirements?.open ?? 0} sub={`${d.requirements?.total ?? 0} total`} to="/ats/requirements" />
        <Stat label="Candidates in Play" value={cand.total ?? 0} sub={`${cand.new_this_week ?? 0} new this week`} to="/ats/candidates" />
        <Stat label="Portal Talent Pool" value={d.portal_candidates?.total ?? 0} sub="public site sign-ups" />
        <Stat label="Interviews" value={d.interviews?.scheduled ?? 0} sub={`${d.interviews?.today ?? 0} today`} to="/ats/interviews" />
        <Stat label="Open Tasks" value={d.tasks?.open ?? 0} sub={`${d.tasks?.overdue ?? 0} overdue`} to="/ats/tasks" />
        {/* Phase 5 (Record the money): was "Open Deals" off a `deals` collection that does not exist (always 0). Now the fee actually earned this month, from `placements`. */}
        <Stat label="Fee Earned This Month" value={inr(d.money?.fee_this_month)} sub={`${d.money?.placements_this_month ?? 0} placed this month - ${d.money?.legacy_invoices_unpaid ?? 0} JM invoices unpaid`} to="/ats/hired-candidates" />
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <Section title="Placement Pipeline" icon={Users} accent="text-blue-600">
          <div className="space-y-2">
            {pipelineOrder.filter((s) => byStatus[s]).map((s) => (
              <div key={s} className="flex items-center gap-3">
                <span className="text-xs text-slate-500 w-24 capitalize">{s.replace('_', ' ')}</span>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-slate-800 rounded-full"
                    style={{ width: `${Math.min(100, (byStatus[s] / Math.max(1, cand.total)) * 100)}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-slate-700 w-8 text-right">{byStatus[s]}</span>
              </div>
            ))}
            {Object.keys(byStatus).length === 0 && <p className="text-sm text-slate-400">No candidates yet.</p>}
          </div>
        </Section>

        <Section title="Needs Your Attention" icon={AlertTriangle} accent="text-amber-500">
          <div className="space-y-2">
            {attention.length === 0 && <p className="text-sm text-slate-400">All clear. Push the pipeline.</p>}
            {attention.map((a, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <span className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${a.kind === 'overdue_task' ? 'bg-red-500' : 'bg-amber-400'}`} />
                <div>
                  <p className="text-slate-700">{a.title}</p>
                  {a.due && <p className="text-xs text-slate-400">due {new Date(a.due).toLocaleDateString()}</p>}
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <Section title="Other Businesses" icon={Boxes} accent="text-emerald-600">
          {feeds.length === 0 ? (
            <p className="text-sm text-slate-400">
              No feeds yet. Automations post here (eSytes billing, outreach engine) via /api/ceo/feeds.
            </p>
          ) : (
            <div className="space-y-4">
              {feeds.map((f) => (
                <div key={f.name}>
                  <p className="text-sm font-medium text-slate-800">{f.title}</p>
                  {(f.lines || []).map((l, i) => (
                    <p key={i} className="text-xs text-slate-600 mt-0.5">{l}</p>
                  ))}
                  {f.updated_at && (
                    <p className="text-[10px] text-slate-400 mt-1">updated {new Date(f.updated_at).toLocaleString()}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Unified From JM" icon={Briefcase} accent="text-indigo-600">
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {[
              ['Clients', imp.clients], ['Requirements', imp.requirements],
              ['Candidates', imp.candidates], ['Interviews', imp.interviews],
              ['Tasks', imp.tasks], ['Emails (history)', imp.emails],
              ['Activity (history)', imp.activity], ['Invoices (history)', imp.invoices],
            ].map(([label, v]) => (
              <div key={label} className="flex justify-between border-b border-slate-100 pb-1">
                <span className="text-slate-500">{label}</span>
                <span className="font-medium text-slate-800">{v ?? 0}</span>
              </div>
            ))}
          </div>
          {(imp.candidates ?? 0) === 0 && (
            <p className="text-xs text-slate-400 mt-3">
              Nothing imported yet — run the JM import from the JM Import page.
            </p>
          )}
        </Section>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Jobs (portal)" value={d.jobs?.total ?? 0} sub={`${d.jobs?.open ?? 0} open`} to="/ats/jobs" />
        <Stat label="Interviews Today" value={d.interviews?.today ?? 0} to="/ats/calendar" />
        <Stat label="Overdue Tasks" value={d.tasks?.overdue ?? 0} to="/ats/tasks" />
        <Stat label="Money Alerts" value={d.money?.legacy_invoices_unpaid ?? 0} to="/ats/money-alerts" />
      </div>
    </div>
  );
}
